import React, { useState } from 'react';
import { DataBody, BrainField, Lead } from '../../types';
import * as api from '../../services/api';
import CsvImportModal from './CsvImportModal';

interface Props {
  dataBody: DataBody;
  fields: BrainField[];
  // Stats are loaded on a separate deferred request from the parent
  // builder. While `statsLoading` is true, BrainSection shows a small
  // pulsing skeleton instead of the totals/today line.
  stats?: Record<string, number> | null;
  statsToday?: Record<string, number> | null;
  statsLoading?: boolean;
  onAddField: (fieldName: string, fieldLabel: string, fieldType: string) => void;
  onToggleRequired: (fieldId: string) => void;
  onRemoveField: (fieldId: string) => void;
}

function BrainSection({
  dataBody,
  fields,
  stats,
  statsToday,
  statsLoading = false,
  onAddField,
  onToggleRequired,
  onRemoveField,
}: Props) {
  const [showAddField, setShowAddField] = useState(false);
  const [newFieldName, setNewFieldName] = useState('');
  const [newFieldLabel, setNewFieldLabel] = useState('');
  const [newFieldType, setNewFieldType] = useState('text');
  const [copied, setCopied] = useState(false);
  const [showLeadsModal, setShowLeadsModal] = useState(false);
  const [leads, setLeads] = useState<Lead[]>([]);
  const [loadingLeads, setLoadingLeads] = useState(false);
  const [showCsvImport, setShowCsvImport] = useState(false);
  // API Log viewer — the lead whose push URL is being shown (null = closed).
  const [apiLogLead, setApiLogLead] = useState<Lead | null>(null);
  const [apiLogCopied, setApiLogCopied] = useState(false);
  // Throttled-hit counters (rate-limit + concurrency rejects). These never
  // create a lead row, so they're surfaced separately from the lead list.
  const [rejections, setRejections] = useState<api.RejectionSummary | null>(null);

  const webhookUrl = `${import.meta.env.REACT_APP_API_URL}/webhook/${dataBody.webhookToken}`;

  async function handleViewLeads() {
    setShowLeadsModal(true);
    setLoadingLeads(true);
    setRejections(null);
    // Fetch the lead list and the throttled-hit counters together. The
    // rejection counters are best-effort — a failure there must not block
    // the lead list from rendering.
    try {
      const [recent, rej] = await Promise.all([
        api.getLeads(dataBody.id, { limit: 20 }),
        api.getRejections(dataBody.id).catch(() => null),
      ]);
      setLeads(recent);
      setRejections(rej);
    } catch (err) {
      console.error('Failed to load leads:', err);
      setLeads([]);
    } finally {
      setLoadingLeads(false);
    }
  }

  function formatReceived(iso: string): string {
    const d = new Date(iso);
    return d.toLocaleString(undefined, { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' });
  }

  /**
   * Which ingestion lane a lead arrived on, for the "API Log" pill.
   *   'api'  — pushed via the webhook URL (source 'webhook' or 'api'); clickable
   *   'csv'  — bulk .csv import; documented but not hyperlinked (no per-row URL)
   *   'none' — legacy rows (source null, pre-migration 017); faint dash
   */
  function apiLogKind(lead: Lead): 'api' | 'csv' | 'none' {
    const s = (lead.source || '').toLowerCase();
    if (s === 'webhook' || s === 'api') {return 'api';}
    if (s === 'csv') {return 'csv';}
    return 'none';
  }

  /**
   * Reconstruct the URL the lead was pushed in on: the body's webhook endpoint
   * plus the raw attributes as query params — i.e. the GET-style / URL push the
   * source made. Derived entirely from data we already store (lead.data + the
   * body's webhook token), so nothing extra is persisted and it ages out with
   * the lead automatically. Enriched values are deliberately excluded — this is
   * the original inbound call, not the post-spine record.
   */
  function buildPushUrl(lead: Lead): string {
    const params = new URLSearchParams();
    const d = lead.data || {};
    for (const [k, v] of Object.entries(d)) {
      if (v === null || v === undefined) {continue;}
      params.append(k, typeof v === 'object' ? JSON.stringify(v) : String(v));
    }
    const qs = params.toString();
    return qs ? `${webhookUrl}?${qs}` : webhookUrl;
  }

  function handleCopyPushUrl(lead: Lead) {
    void navigator.clipboard.writeText(buildPushUrl(lead));
    setApiLogCopied(true);
    setTimeout(() => setApiLogCopied(false), 2000);
  }

  function statusColor(status: string): { bg: string; fg: string } {
    if (status === 'completed' || status === 'delivered') {return { bg: '#dcfce7', fg: '#166534' };}
    if (status === 'failed' || status === 'delivery_failed') {return { bg: '#fee2e2', fg: '#991b1b' };}
    if (status === 'partial_delivery') {return { bg: '#fef3c7', fg: '#92400e' };}
    if (status === 'processing') {return { bg: '#fef3c7', fg: '#92400e' };}
    if (status === 'queued_for_delivery') {return { bg: '#e0e7ff', fg: '#3730a3' };} // indigo — distinct from received-blue and processing-amber
    return { bg: '#dbeafe', fg: '#1e40af' }; // received / unknown
  }

  /**
   * Per-leg counts on a delivered/failed/partial lead. Returns the human-
   * readable suffix shown after the status word in the badge — "2/4",
   * "4/4", "0/4" — or null if the lead never made it to the legs (in
   * which case the badge is just the bare status word).
   */
  function legsCountSuffix(lead: Lead): string | null {
    const att = lead.legsAttempted ?? 0;
    if (att <= 0) {return null;}
    const ok = lead.legsSucceeded ?? 0;
    return `${ok}/${att}`;
  }

  /**
   * Display status word — collapses partial_delivery to "partial" and
   * delivery_failed to "failed" so the pill stays compact. queued_for_delivery
   * shows as "queued" — the lead is sitting in a per-destination BullMQ queue
   * waiting for the worker to fire (or in flight at the destination).
   */
  function displayStatus(status: string): string {
    if (status === 'partial_delivery') {return 'partial';}
    if (status === 'delivery_failed') {return 'failed';}
    if (status === 'queued_for_delivery') {return 'queued';}
    return status;
  }

  function handleCopy() {
    void navigator.clipboard.writeText(webhookUrl);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  }

  function handleAddField() {
    if (!newFieldName.trim() || !newFieldLabel.trim()) {return;}
    onAddField(
      newFieldName.toLowerCase().replace(/\s+/g, '_'),
      newFieldLabel,
      newFieldType
    );
    setNewFieldName('');
    setNewFieldLabel('');
    setNewFieldType('text');
    setShowAddField(false);
  }

  return (
    <div className="brain-section">
      <div className="brain-title">
        <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <circle cx="12" cy="12" r="10"/>
          <path d="M12 16v-4"/>
          <path d="M12 8h.01"/>
        </svg>
        THE BRAIN
      </div>
      <div className="brain-subtitle">Input Configuration & Data Mapping</div>

      {/* Webhook URL */}
      <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginBottom: 16 }}>
        <div className="webhook-url" style={{ flex: 1, marginBottom: 0 }}>
          {webhookUrl}
        </div>
        <button className="copy-btn" onClick={handleCopy}>
          {copied ? 'Copied!' : 'Copy'}
        </button>
      </div>

      {/* Field List */}
      <div className="field-list">
        {(() => {
          // Stats are loaded asynchronously by the parent builder. Render
          // a small skeleton placeholder while they're inbound so the
          // header layout stays stable.
          // "Received" = every lead that came in (all statuses, including
          // spine-rejected). "Sent" = leads that passed ALL spine/vertebra
          // filters and were dispatched to the legs — destination responses
          // (delivered vs delivery_failed) don't change whether it was sent.
          // So "sent" is every post-spine status; it excludes received,
          // processing (still in the spine) and failed (rejected by the spine).
          const SENT_STATUSES = ['completed', 'delivered', 'partial_delivery', 'delivery_failed', 'queued_for_delivery'];
          const sumAll = (o: Record<string, number>) => Object.values(o).reduce((a, b) => a + (b || 0), 0);
          const sumSent = (o: Record<string, number>) => SENT_STATUSES.reduce((a, s) => a + (o[s] || 0), 0);
          const received = stats ? sumAll(stats) : null;
          const sent = stats ? sumSent(stats) : null;
          const receivedToday = statsToday ? sumAll(statsToday) : null;
          const sentToday = statsToday ? sumSent(statsToday) : null;
          return (
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: 8 }}>
              <div style={{ fontSize: 12, fontWeight: 600, color: '#9ca3af', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                Data Points ({fields.length} / 300)
              </div>
              {statsLoading ? (
                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 4 }}>
                  <span
                    className="stat-skeleton"
                    style={{ width: 80, height: 11, borderRadius: 3 }}
                    aria-label="loading totals"
                  />
                  <span
                    className="stat-skeleton"
                    style={{ width: 60, height: 11, borderRadius: 3 }}
                    aria-label="loading today"
                  />
                </div>
              ) : received !== null ? (
                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 2 }}>
                  <div
                    style={{ fontSize: 11, color: '#9ca3af', fontWeight: 500 }}
                    title="Total leads received by this brain across all statuses — includes leads rejected by the spine filters."
                  >
                    {received.toLocaleString()} received{receivedToday ? ` · ${receivedToday.toLocaleString()} today` : ''}
                  </div>
                  {sent !== null && (
                    <div
                      style={{ fontSize: 11, color: '#6366f1', fontWeight: 500 }}
                      title="Leads that passed all spine (vertebra) filters and were dispatched to the legs. Destination responses don't affect this count."
                    >
                      {sent.toLocaleString()} sent{sentToday ? ` · ${sentToday.toLocaleString()} today` : ''}
                    </div>
                  )}
                </div>
              ) : null}
            </div>
          );
        })()}

        {fields.length === 0 ? (
          <div style={{
            padding: '20px 16px',
            textAlign: 'center',
            color: '#9ca3af',
            fontSize: 13,
            background: '#f8f9fb',
            borderRadius: 8,
            border: '1px dashed #d1d5db',
          }}>
            <div style={{ marginBottom: 6, fontSize: 20 }}>~</div>
            <div style={{ fontWeight: 500, color: '#6b7280', marginBottom: 4 }}>No data points yet</div>
            <div>POST data to the webhook above and fields will be auto-discovered, or add them manually below.</div>
          </div>
        ) : (
          // Once a brain grows past 15 data points (enrichment can add 100+),
          // cap the list height and let it scroll so the body page stays tidy.
          // Below the threshold it renders inline with no scrollbar.
          <div
            style={fields.length > 15
              ? { maxHeight: 720, overflowY: 'auto', paddingRight: 4, marginRight: -4 }
              : undefined}
          >
          {fields.map(field => (
            <div key={field.id} className="field-item">
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <span className="field-name">{field.fieldLabel}</span>
                <span className="field-type">{field.fieldType}</span>
                {field.source === 'discovered' && (
                  <span style={{
                    fontSize: 10,
                    padding: '1px 5px',
                    borderRadius: 4,
                    background: '#dbeafe',
                    color: '#2563eb',
                    fontWeight: 500,
                  }}>auto</span>
                )}
                {field.isRequired && <span className="field-required">Required</span>}
              </div>
              <div style={{ display: 'flex', gap: 4, alignItems: 'center' }}>
                <button
                  className="btn-icon"
                  title={field.isRequired ? 'Make optional' : 'Make required'}
                  onClick={() => onToggleRequired(field.id)}
                  style={{ width: 28, height: 28, fontSize: 12 }}
                >
                  {field.isRequired ? 'R' : 'O'}
                </button>
                <button
                  className="btn-icon"
                  title="Remove field"
                  onClick={() => onRemoveField(field.id)}
                  style={{ width: 28, height: 28, fontSize: 14, color: '#ef4444' }}
                >
                  x
                </button>
              </div>
            </div>
          ))}
          </div>
        )}

        {/* Add Custom Field */}
        {showAddField ? (
          <div style={{ marginTop: 12, padding: 12, background: '#f8f9fb', borderRadius: 8 }}>
            <div className="form-group" style={{ marginBottom: 8 }}>
              <input
                className="form-input"
                placeholder="Field label (e.g., Company Name)"
                value={newFieldLabel}
                onChange={e => {
                  setNewFieldLabel(e.target.value);
                  setNewFieldName(e.target.value.toLowerCase().replace(/\s+/g, '_'));
                }}
                autoFocus
              />
            </div>
            <div className="form-row">
              <select
                className="form-select"
                value={newFieldType}
                onChange={e => setNewFieldType(e.target.value)}
                style={{ flex: 1 }}
              >
                <option value="text">Text</option>
                <option value="email">Email</option>
                <option value="phone">Phone</option>
                <option value="number">Number</option>
                <option value="boolean">Boolean</option>
                <option value="address">Address</option>
              </select>
              <button className="btn btn-primary btn-sm" onClick={handleAddField}>Add</button>
              <button className="btn btn-secondary btn-sm" onClick={() => setShowAddField(false)}>Cancel</button>
            </div>
          </div>
        ) : (
          <div style={{ display: 'flex', gap: 8, marginTop: 8 }}>
            <button
              className="btn btn-secondary btn-sm"
              style={{ flex: 1, justifyContent: 'center' }}
              onClick={() => setShowAddField(true)}
            >
              + Add Field
            </button>
            <button
              className="btn btn-secondary btn-sm"
              style={{ flex: 1, justifyContent: 'center', display: 'flex', alignItems: 'center', gap: 4 }}
              onClick={() => setShowCsvImport(true)}
              title="Upload leads from a .csv file"
            >
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/>
                <polyline points="17 8 12 3 7 8"/>
                <line x1="12" y1="3" x2="12" y2="15"/>
              </svg>
              Upload CSV
            </button>
            <button
              className="btn btn-secondary btn-sm"
              style={{ flex: 1, justifyContent: 'center' }}
              onClick={handleViewLeads}
            >
              View Leads
            </button>
          </div>
        )}
      </div>

      {/* CSV Import Modal */}
      <CsvImportModal
        open={showCsvImport}
        dataBodyId={dataBody.id}
        dataBodyName={dataBody.name}
        onClose={() => setShowCsvImport(false)}
      />

      {/* View Leads Modal */}
      {showLeadsModal && (
        <div
          className="modal-overlay"
          onClick={() => setShowLeadsModal(false)}
        >
          <div
            onClick={e => e.stopPropagation()}
            style={{
              background: 'white',
              borderRadius: 12,
              maxWidth: '90vw',
              width: 980,
              maxHeight: '85vh',
              overflow: 'hidden',
              display: 'flex',
              flexDirection: 'column',
              boxShadow: '0 20px 60px rgba(0,0,0,0.25)',
            }}
          >
            {/* Header */}
            <div style={{
              padding: '18px 24px',
              borderBottom: '1px solid #e5e7eb',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              flexShrink: 0,
            }}>
              <div>
                <h2 style={{ margin: 0, fontSize: 18, fontWeight: 600 }}>Recent Leads</h2>
                <div style={{ fontSize: 12, color: '#9ca3af', marginTop: 4 }}>
                  Last 20 leads &middot; showing {leads.length}
                </div>
              </div>
              <button
                onClick={() => setShowLeadsModal(false)}
                style={{
                  background: 'none',
                  border: 'none',
                  fontSize: 24,
                  cursor: 'pointer',
                  color: '#9ca3af',
                  padding: 0,
                  width: 32,
                  height: 32,
                  lineHeight: 1,
                }}
                aria-label="Close"
              >&times;</button>
            </div>

            {/* Throttled-hit banner. Rate-limit / concurrency rejections are
                dropped at the door with NO lead row (so a flood can't write
                millions of rows) — this surfaces the per-day counters so the
                throttling is still visible. Only shown when there's something
                to report. Counts are UTC-day buckets. */}
            {rejections && (rejections.rateLimit.last7Days > 0 || rejections.concurrency.last7Days > 0) && (
              <div style={{
                flexShrink: 0,
                padding: '10px 24px',
                background: '#fff7ed',
                borderBottom: '1px solid #fed7aa',
                display: 'flex',
                alignItems: 'center',
                gap: 14,
                flexWrap: 'wrap',
                fontSize: 12,
              }}>
                <span style={{ display: 'flex', alignItems: 'center', gap: 6, color: '#9a3412', fontWeight: 600 }}>
                  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M10.29 3.86 1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"/>
                    <line x1="12" y1="9" x2="12" y2="13"/>
                    <line x1="12" y1="17" x2="12.01" y2="17"/>
                  </svg>
                  Throttled — dropped at the door, no lead row:
                </span>
                {rejections.rateLimit.last7Days > 0 && (
                  <span style={{ color: '#7c2d12' }}>
                    <strong>Rate-limit</strong>{' '}
                    {rejections.rateLimit.today.toLocaleString()} today
                    <span style={{ opacity: 0.7 }}> · {rejections.rateLimit.last7Days.toLocaleString()} in 7d</span>
                  </span>
                )}
                {rejections.concurrency.last7Days > 0 && (
                  <span style={{ color: '#7c2d12' }}>
                    <strong>Concurrency</strong>{' '}
                    {rejections.concurrency.today.toLocaleString()} today
                    <span style={{ opacity: 0.7 }}> · {rejections.concurrency.last7Days.toLocaleString()} in 7d</span>
                  </span>
                )}
                <span style={{ marginLeft: 'auto', color: '#c2662b', fontSize: 11 }} title="Counts are bucketed by UTC day (that's how they're recorded at reject time).">
                  UTC-day counts
                </span>
              </div>
            )}

            {/* Body */}
            <div style={{ overflow: 'auto', flex: 1, background: '#fff' }}>
              {loadingLeads ? (
                <div style={{ padding: 60, textAlign: 'center', color: '#9ca3af', fontSize: 14 }}>
                  Loading leads...
                </div>
              ) : leads.length === 0 ? (
                <div style={{ padding: 60, textAlign: 'center', color: '#9ca3af', fontSize: 14 }}>
                  No leads yet. Once data is sent to the webhook, the most recent 20 will show up here.
                </div>
              ) : (
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                  <thead>
                    <tr style={{ position: 'sticky', top: 0, background: '#f8f9fb', zIndex: 1, boxShadow: 'inset 0 -1px 0 #e5e7eb' }}>
                      <th style={{ padding: '10px 12px', textAlign: 'left', fontWeight: 600, color: '#6b7280', fontSize: 11, textTransform: 'uppercase', letterSpacing: '0.04em', whiteSpace: 'nowrap' }}>Received</th>
                      <th style={{ padding: '10px 12px', textAlign: 'left', fontWeight: 600, color: '#6b7280', fontSize: 11, textTransform: 'uppercase', letterSpacing: '0.04em', whiteSpace: 'nowrap' }}>API Log</th>
                      <th style={{ padding: '10px 12px', textAlign: 'left', fontWeight: 600, color: '#6b7280', fontSize: 11, textTransform: 'uppercase', letterSpacing: '0.04em', whiteSpace: 'nowrap' }}>Status</th>
                      <th style={{ padding: '10px 12px', textAlign: 'left', fontWeight: 600, color: '#6b7280', fontSize: 11, textTransform: 'uppercase', letterSpacing: '0.04em', whiteSpace: 'nowrap' }}>Fail Reason</th>
                      {fields.map(f => (
                        <th
                          key={f.id}
                          style={{ padding: '10px 12px', textAlign: 'left', fontWeight: 600, color: '#6b7280', fontSize: 11, textTransform: 'uppercase', letterSpacing: '0.04em', whiteSpace: 'nowrap' }}
                        >
                          {f.fieldLabel}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {leads.map((lead, i) => {
                      const sc = statusColor(lead.status);
                      return (
                        <tr key={lead.id} style={{ borderBottom: '1px solid #f3f4f6', background: i % 2 === 0 ? '#fff' : '#fafbfc' }}>
                          <td style={{ padding: '10px 12px', whiteSpace: 'nowrap', color: '#6b7280' }}>{formatReceived(lead.receivedAt)}</td>
                          <td style={{ padding: '10px 12px', whiteSpace: 'nowrap' }}>
                            {(() => {
                              const kind = apiLogKind(lead);
                              if (kind === 'api') {
                                return (
                                  <button
                                    onClick={() => { setApiLogCopied(false); setApiLogLead(lead); }}
                                    title="View the API call that pushed this lead in"
                                    style={{
                                      display: 'inline-block',
                                      padding: '2px 8px',
                                      borderRadius: 4,
                                      background: '#e0e7ff',
                                      color: '#3730a3',
                                      fontSize: 11,
                                      fontWeight: 600,
                                      border: 'none',
                                      cursor: 'pointer',
                                      letterSpacing: '0.03em',
                                    }}
                                  >API</button>
                                );
                              }
                              if (kind === 'csv') {
                                return (
                                  <span
                                    title="Imported from a .csv file"
                                    style={{
                                      display: 'inline-block',
                                      padding: '2px 8px',
                                      borderRadius: 4,
                                      background: '#f3f4f6',
                                      color: '#9ca3af',
                                      fontSize: 11,
                                      fontWeight: 600,
                                      letterSpacing: '0.03em',
                                    }}
                                  >CSV</span>
                                );
                              }
                              return <span style={{ color: '#d1d5db' }}>&mdash;</span>;
                            })()}
                          </td>
                          <td style={{ padding: '10px 12px', whiteSpace: 'nowrap' }}>
                            <span style={{
                              display: 'inline-block',
                              padding: '2px 8px',
                              borderRadius: 4,
                              background: sc.bg,
                              color: sc.fg,
                              fontSize: 11,
                              fontWeight: 500,
                            }}>
                              {displayStatus(lead.status)}
                              {legsCountSuffix(lead) && (
                                <span style={{ marginLeft: 5, opacity: 0.85, fontVariantNumeric: 'tabular-nums' }}>
                                  {legsCountSuffix(lead)}
                                </span>
                              )}
                            </span>
                          </td>
                          <td
                            style={{ padding: '10px 12px', color: '#991b1b', fontSize: 12, maxWidth: 280, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
                            title={lead.failReason || ''}
                          >
                            {lead.failReason || <span style={{ color: '#d1d5db' }}>&mdash;</span>}
                          </td>
                          {fields.map(f => {
                            // Prefer enriched values (added by vertebrae like
                            // Identity Enrichment) over the raw incoming data —
                            // same precedence as the CSV export's COALESCE.
                            const v = lead.enrichedData?.[f.fieldName] ?? lead.data?.[f.fieldName];
                            return (
                              <td key={f.id} style={{ padding: '10px 12px', color: '#111827', maxWidth: 220, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                {v != null && v !== '' ? String(v) : <span style={{ color: '#d1d5db' }}>&mdash;</span>}
                              </td>
                            );
                          })}
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              )}
            </div>
          </div>
        </div>
      )}

      {/* API Log viewer — the reconstructed push URL for a single lead.
          Renders above the Recent Leads modal (higher z-index). */}
      {apiLogLead && (
        <div
          className="modal-overlay"
          onClick={() => setApiLogLead(null)}
          style={{ zIndex: 1000 }}
        >
          <div
            onClick={e => e.stopPropagation()}
            style={{
              background: 'white',
              borderRadius: 12,
              maxWidth: '90vw',
              width: 640,
              boxShadow: '0 20px 60px rgba(0,0,0,0.25)',
              overflow: 'hidden',
            }}
          >
            <div style={{
              padding: '18px 24px',
              borderBottom: '1px solid #e5e7eb',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
            }}>
              <div>
                <h2 style={{ margin: 0, fontSize: 16, fontWeight: 600 }}>API Log</h2>
                <div style={{ fontSize: 12, color: '#9ca3af', marginTop: 4 }}>
                  How this lead was pushed in &middot; {formatReceived(apiLogLead.receivedAt)}
                </div>
              </div>
              <button
                onClick={() => setApiLogLead(null)}
                style={{ background: 'none', border: 'none', fontSize: 24, cursor: 'pointer', color: '#9ca3af', padding: 0, width: 32, height: 32, lineHeight: 1 }}
                aria-label="Close"
              >&times;</button>
            </div>
            <div style={{ padding: 20 }}>
              <div style={{ fontSize: 11, fontWeight: 600, color: '#9ca3af', textTransform: 'uppercase', letterSpacing: '0.04em', marginBottom: 8 }}>
                Push URL
              </div>
              <div style={{
                fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace',
                fontSize: 12,
                lineHeight: 1.6,
                color: '#111827',
                background: '#f8f9fb',
                border: '1px solid #e5e7eb',
                borderRadius: 8,
                padding: 12,
                wordBreak: 'break-all',
                maxHeight: 260,
                overflowY: 'auto',
              }}>
                {buildPushUrl(apiLogLead)}
              </div>
              <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 12 }}>
                <button className="btn btn-primary btn-sm" onClick={() => handleCopyPushUrl(apiLogLead)}>
                  {apiLogCopied ? 'Copied!' : 'Copy URL'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default BrainSection;
